"use client";

import { useEffect, useState } from "react";
import { play, setEnabled } from "cuelume";

type FeedbackMode = "off" | "important" | "enhanced";
const STORAGE_KEY = "scarlet-interaction-feedback";

/** Applies the Design System's calm feedback policy to direct button activations. */
export default function InteractionFeedback() {
  const [mode, setMode] = useState<FeedbackMode>("enhanced");

  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const initialMode: FeedbackMode = stored === "off" || stored === "important" || stored === "enhanced" ? stored : "enhanced";
    if (!stored) window.localStorage.setItem(STORAGE_KEY, initialMode);
    setMode(initialMode);

    const handlePreferenceChange = (event: Event) => {
      const nextMode = (event as CustomEvent<FeedbackMode>).detail;
      if (nextMode === "off" || nextMode === "important" || nextMode === "enhanced") {
        setMode(nextMode);
      }
    };

    window.addEventListener("scarlet-interaction-feedback-change", handlePreferenceChange);
    return () => window.removeEventListener("scarlet-interaction-feedback-change", handlePreferenceChange);
  }, []);

  useEffect(() => {
    setEnabled(mode !== "off");

    const onClick = (event: MouseEvent) => {
      if (mode === "off") return;
      const target = event.target as Element | null;
      const control = target?.closest("button:not(:disabled), [role='button']:not([aria-disabled='true'])");
      if (!control) return;
      const isImportant =
        control.matches("[data-interaction-feedback='important'], [type='submit']") ||
        control.classList.contains("btn-primary") ||
        Array.from(control.classList).some((className) => /(^|:)bg-(red|rose)-/.test(className));
      if (mode === "important" && !isImportant) return;
      play("tick");
      if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches && "vibrate" in navigator) {
        navigator.vibrate(6);
      }
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [mode]);

  return null;
}
